import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminLogin, adminLogout, getAdminSession } from '../api';
import AdminProfile from './AdminProfile';
import AdminWorks from './AdminWorks';
import AdminLanyard from './AdminLanyard';

export default function Admin() {
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [loginForm, setLoginForm] = useState({
    username: 'admin',
    password: '',
  });
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { id: 'profile', label: '个人信息', icon: '👤' },
    { id: 'lanyard', label: '吊牌编辑', icon: '🎟️' },
    { id: 'works', label: '作品与项目', icon: '🎨' },
  ];

  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await getAdminSession();
        setIsAuthenticated(Boolean(res.data.authenticated));
        setUsername(res.data.username || '');
        if (res.data.username) {
          setLoginForm((prev) => ({ ...prev, username: res.data.username }));
        }
      } catch (error) {
        console.error('Failed to check admin session:', error);
      } finally {
        setAuthChecked(true);
      }
    };

    checkSession();
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <AdminProfile />;
      case 'works':
        return <AdminWorks />;
      case 'lanyard':
        return <AdminLanyard />;
      default:
        return <AdminProfile />;
    }
  };

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoggingIn(true);
    setLoginError('');

    try {
      const res = await adminLogin(loginForm);
      setIsAuthenticated(true);
      setUsername(res.data.username || loginForm.username);
      setLoginForm((prev) => ({ ...prev, password: '' }));
    } catch (error) {
      setLoginError(error?.response?.data?.error || '登录失败，请检查账号和密码');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
    } catch (error) {
      console.error('Failed to log out:', error);
    } finally {
      setIsAuthenticated(false);
      setUsername('');
      setLoginForm((prev) => ({ ...prev, password: '' }));
    }
  };

  if (!authChecked) {
    return <div className="min-h-screen bg-dark flex items-center justify-center text-white text-xl">加载中...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-dark-secondary border border-gray-700 rounded-2xl p-8">
          <div className="mb-8">
            <p className="text-sm uppercase tracking-[0.3em] text-gray-400 mb-3">Private Admin</p>
            <h1 className="text-3xl font-bold text-white mb-3">进入个人网站后台</h1>
            <p className="text-gray-400 leading-relaxed">
              这里是你的私人编辑入口。登录后就可以实时更新个人信息、经历、项目和亮点内容。
            </p>
          </div>

          {loginError && (
            <div className="mb-4 rounded-lg border border-red-800 bg-red-950/60 px-4 py-3 text-sm text-red-200">
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-white mb-2">用户名</label>
              <input
                type="text"
                name="username"
                value={loginForm.username}
                onChange={handleLoginChange}
                className="w-full rounded-lg border border-gray-700 bg-dark px-4 py-3 text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-2">密码</label>
              <input
                type="password"
                name="password"
                value={loginForm.password}
                onChange={handleLoginChange}
                required
                className="w-full rounded-lg border border-gray-700 bg-dark px-4 py-3 text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <button
              type="submit"
              disabled={loggingIn}
              className="w-full rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
            >
              {loggingIn ? '登录中...' : '进入后台'}
            </button>
          </form>

          <Link
            to="/"
            className="mt-6 inline-flex text-sm font-semibold text-gray-300 transition hover:text-white"
          >
            ← 返回网站首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark flex flex-col lg:flex-row">
      {/* Sidebar */}
      <div className="w-full lg:w-64 lg:shrink-0 bg-dark-secondary border-r border-gray-700 p-6">
        <h1 className="text-2xl font-bold text-white mb-8">管理后台</h1>

        <div className="mb-8 rounded-xl border border-gray-700 bg-dark p-4">
          <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-2">Signed In</p>
          <p className="text-white font-semibold break-all">{username || 'admin'}</p>
        </div>

        <nav className="flex flex-wrap gap-2 lg:block lg:space-y-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 lg:w-full text-left px-4 py-3 rounded-lg font-semibold transition ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:bg-dark hover:text-white'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="mt-12 pt-6 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="block w-full px-4 py-3 rounded-lg font-semibold text-gray-300 hover:bg-dark hover:text-white transition text-center mb-3"
          >
            退出登录
          </button>
          <Link
            to="/"
            className="block px-4 py-3 rounded-lg font-semibold text-gray-300 hover:bg-dark hover:text-white transition text-center"
          >
            ← 返回网站
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="min-w-0 flex-1 overflow-auto">
        {renderContent()}
      </div>
    </div>
  );
}
